/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import cx from "classnames";
import React from "react";

import { LoaderSpinner } from "../LoaderSpinner";

import "./MediaButton.scss";

export type IMediaButtonVariant =
  | "plain"
  | "primary"
  | "secondary"
  | "transparent"
  | "link"
  | "disabled"
  | "unfilled";

export interface IMediaButtonProps {
  className?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconElevated?: boolean;
  type?: "button" | "submit" | "reset" | undefined;
  size?: "large" | "middle" | "small" | undefined;
  variant: IMediaButtonVariant;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode | string;
  loading?: boolean;
  id?: string;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
  ariaLabel?: string;
  tabIndex?: number;
}

export const MediaButton = React.forwardRef<
  HTMLButtonElement,
  IMediaButtonProps
>(
  (
    {
      className,
      children,
      disabled,
      icon,
      iconElevated,
      loading,
      type,
      variant,
      size = "middle",
      onClick,
      onMouseEnter,
      onMouseLeave,
      ariaLabel,
      tabIndex,
      ...props
    }: IMediaButtonProps,
    ref,
  ) => {
    return (
      <button
        className={cx(
          "MediaButton",
          `MediaButton--${variant} MediaButton--${size}`,
          className,
          {
            "MediaButton--loading": loading,
            "MediaButton--disabled": disabled,
          },
        )}
        disabled={disabled || loading}
        type={type}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={ref}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        {...props}
      >
        {loading && (
          <LoaderSpinner
            className="MediaButton__LoaderSpinner"
            width={25}
            height={25}
          />
        )}
        {icon && (
          <i
            className={cx("MediaButton__icon", {
              "MediaButton__icon--elevated": iconElevated,
            })}
            aria-hidden="true"
          >
            {icon}
          </i>
        )}
        {children && <span className="MediaButton__text">{children}</span>}
      </button>
    );
  },
);
